import type { ReactNode } from 'react';
import { Heatmap } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { CustomerVolume } from '../types';
import { weekdayRowOf, type LinkFocus } from './link';
import { SeeTheNumbers } from './SeeTheNumbers';
import { UsageCard } from './UsageCard';
import { hourGrid, hourWord, weekdayWords } from './volume-math';
import './insights.css';

/** A heading every third hour, short enough to fit over a cell. */
const HOUR_HEADINGS = Array.from({ length: 24 }, (_, h) =>
  h % 3 === 0 || h === 8 || h === 18
    ? h === 0
      ? '12a'
      : h === 12
        ? '12p'
        : h < 12
          ? `${h}a`
          : `${h - 12}p`
    : '',
);

interface Cell {
  weekday: number;
  hour: number;
  pages: number;
}

function cellsOf(grid: readonly (readonly number[])[]): Cell[] {
  return grid.flatMap((row, r) => row.map((pages, hour) => ({ weekday: r, hour, pages })));
}

function span(c: Cell): string {
  return `${weekdayWords[c.weekday]}, ${hourWord(c.hour)} to ${hourWord((c.hour + 1) % 24)}`;
}

/** The busiest hour, and how much arrives in office hours (weekdays, 8 am to 6 pm). */
function Summary({ cells, total, f }: { cells: readonly Cell[]; total: number; f: Formatter }) {
  const busiest = cells.reduce(
    (best, c) => (c.pages > best.pages ? c : best),
    cells[0] ?? { weekday: 0, hour: 0, pages: 0 },
  );
  const office = cells
    .filter((c) => c.weekday < 5 && c.hour >= 8 && c.hour < 18)
    .reduce((s, c) => s + c.pages, 0);
  return (
    <p className="ad-vol__summary">
      Busiest: <strong>{span(busiest)}</strong> ({f.int(busiest.pages)} pages).{' '}
      <strong>{f.percent((office / total) * 100, 0)}</strong> arrive on weekdays between 8 am and 6
      pm.
    </p>
  );
}

function numbersOf(cells: readonly Cell[], total: number, f: Formatter) {
  return cells
    .filter((c) => c.pages > 0)
    .map((c) => ({
      key: `${c.weekday}-${c.hour}`,
      cells: [span(c), f.int(c.pages), f.percent((c.pages / total) * 100)],
      values: [c.weekday * 24 + c.hour, c.pages, c.pages],
    }));
}

const NUMBER_COLUMNS = [
  { heading: 'Hour' },
  { heading: 'Pages', numeric: true },
  { heading: 'Share', numeric: true },
];

export interface WorkHoursCardProps {
  volume: CustomerVolume;
  /** The days the grid covers (the page's range). */
  days: number;
  focus: LinkFocus;
  onFocus: (focus: LinkFocus) => void;
}

/** When the pages arrive: weekday by hour over the page's range, in the company's own time zone. */
export function WorkHoursCard({ volume, days, focus, onFocus }: WorkHoursCardProps): ReactNode {
  const f = useFormat();
  const grid = hourGrid(volume.hours);
  const cells = cellsOf(grid);
  const total = cells.reduce((s, c) => s + c.pages, 0);
  const max = Math.max(0, ...cells.map((c) => c.pages));
  return (
    <UsageCard
      id="ad-hours"
      title="When they work"
      note={`Pages by weekday and hour over the last ${days} days, in their time (${volume.timeZone})`}
      empty={total === 0 ? `No pages in the last ${days} days.` : null}
    >
      <Summary cells={cells} total={total} f={f} />
      <Heatmap
        rows={weekdayWords}
        columns={HOUR_HEADINGS}
        values={grid}
        label={`Pages by weekday and hour over ${days} days; darker is more.`}
        legendHigh={`${f.int(max)} pages an hour`}
        highlightRow={weekdayRowOf(focus)}
        onActiveRow={(row) => onFocus(row === null ? null : { kind: 'weekday', weekday: row + 1 })}
        tip={(r, c, v) => ({
          value: `${f.int(v)} pages`,
          title: span({ weekday: r, hour: c, pages: v }),
          lines: [`${f.percent((v / total) * 100)} of the range's pages`],
        })}
      />
      <SeeTheNumbers
        caption="Pages by weekday and hour"
        columns={NUMBER_COLUMNS}
        rows={numbersOf(cells, total, f)}
      />
    </UsageCard>
  );
}
